'use client';

/**
 * LodgeSearchBar — Airbnb-style search bar for /booking/lodges.
 *
 * Dates (range) + guests go first, matching lodges come after. This
 * component only owns the input UI; the parent page is responsible for
 * calling `searchLodges()` (lib/booking/search-service.ts) and rendering
 * results.
 *
 * NOT built on top of BookingCalendar (that component is coupled to a
 * single lodge/activity item + Guesty availability fetching). This is a
 * plain range picker over react-day-picker, same library/pattern used by
 * PackageBookingClient.tsx (mode="single" there, "range" here).
 */

import { useState, useEffect, useRef, useCallback, useMemo, useId } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { DayPicker, type DateRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format, parseISO, isValid } from 'date-fns';
import { es, enUS, pt } from 'date-fns/locale';
import styles from './LodgeSearchBar.module.css';

export interface LodgeSearchBarProps {
  initialCheckIn?: string; // YYYY-MM-DD
  initialCheckOut?: string; // YYYY-MM-DD
  initialGuests?: number;
  loading?: boolean;
  onSearch: (params: { checkIn: string; checkOut: string; guests: number }) => void;
  onClear: () => void;
}

const MIN_GUESTS = 1;
const MAX_GUESTS = 12;
const DEFAULT_GUESTS = 2;

function parseDateParam(value?: string): Date | undefined {
  if (!value) return undefined;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
}

function clampGuests(value: number): number {
  return Math.max(MIN_GUESTS, Math.min(MAX_GUESTS, value));
}

export default function LodgeSearchBar({
  initialCheckIn,
  initialCheckOut,
  initialGuests = DEFAULT_GUESTS,
  loading = false,
  onSearch,
  onClear,
}: LodgeSearchBarProps) {
  const t = useTranslations('bookingLodges.search');
  const locale = useLocale() as 'en' | 'es' | 'pt';
  const dateFnsLocale = locale === 'es' ? es : locale === 'pt' ? pt : enUS;
  const dialogId = useId();

  const [range, setRange] = useState<DateRange | undefined>(() => ({
    from: parseDateParam(initialCheckIn),
    to: parseDateParam(initialCheckOut),
  }));
  const [guests, setGuests] = useState<number>(clampGuests(initialGuests));
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Reflect external changes to the search (e.g. parent syncs from the URL,
  // or resets after a "clear") back into the picker's local state.
  useEffect(() => {
    setRange({
      from: parseDateParam(initialCheckIn),
      to: parseDateParam(initialCheckOut),
    });
  }, [initialCheckIn, initialCheckOut]);

  useEffect(() => {
    setGuests(clampGuests(initialGuests));
  }, [initialGuests]);

  // 2 months on desktop, 1 on mobile.
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Close the popover on outside click.
  useEffect(() => {
    if (!isOpen) return;
    function handlePointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setIsOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  // Close the popover on Escape.
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleGuestsChange = useCallback((next: number) => {
    setGuests(clampGuests(next));
  }, []);

  const formattedCheckIn = range?.from ? format(range.from, 'd MMM yyyy', { locale: dateFnsLocale }) : null;
  const formattedCheckOut = range?.to ? format(range.to, 'd MMM yyyy', { locale: dateFnsLocale }) : null;
  const hasDates = Boolean(range?.from && range?.to);
  const hasActiveSearch = Boolean(initialCheckIn && initialCheckOut);
  const canSearch = hasDates && !loading;

  const guestsDisplay = useMemo(() => t('guestsLabel', { count: guests }), [t, guests]);

  const handleSearch = useCallback(() => {
    if (!range?.from || !range?.to) return;
    onSearch({
      checkIn: format(range.from, 'yyyy-MM-dd'),
      checkOut: format(range.to, 'yyyy-MM-dd'),
      guests,
    });
    setIsOpen(false);
  }, [range, guests, onSearch]);

  const handleClear = useCallback(() => {
    setRange({ from: undefined, to: undefined });
    setIsOpen(false);
    onClear();
  }, [onClear]);

  return (
    <div className={styles.searchBar}>
      <div className={styles.fieldsGroup}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.datesField}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={dialogId}
        >
          {range?.from ? (
            <>
              <span className={styles.dateSegment}>
                <span className={styles.fieldCaption}>{t('checkIn')}</span>
                <span className={styles.fieldValue}>{formattedCheckIn}</span>
              </span>
              <span className={styles.dateDivider} aria-hidden="true" />
              <span className={styles.dateSegment}>
                <span className={styles.fieldCaption}>{t('checkOut')}</span>
                {/* checkOut not picked yet (range mid-selection) — show a dash
                    instead of reverting to the generic placeholder, so the
                    already-picked check-in date stays visible. */}
                <span className={styles.fieldValue}>{formattedCheckOut ?? '—'}</span>
              </span>
            </>
          ) : (
            <span className={styles.datesPlaceholder}>{t('datesPlaceholder')}</span>
          )}
        </button>

        <div className={styles.guestsField} role="group" aria-label={guestsDisplay}>
          <button
            type="button"
            className={styles.stepperButton}
            onClick={() => handleGuestsChange(guests - 1)}
            disabled={guests <= MIN_GUESTS}
            aria-label={t('guestsLabel', { count: clampGuests(guests - 1) })}
          >
            −
          </button>
          <span className={styles.guestsValue}>{guestsDisplay}</span>
          <button
            type="button"
            className={styles.stepperButton}
            onClick={() => handleGuestsChange(guests + 1)}
            disabled={guests >= MAX_GUESTS}
            aria-label={t('guestsLabel', { count: clampGuests(guests + 1) })}
          >
            +
          </button>
        </div>

        {isOpen && (
          <div ref={popoverRef} id={dialogId} className={styles.popover} role="dialog" aria-label={`${t('checkIn')} / ${t('checkOut')}`}>
            <DayPicker
              mode="range"
              selected={range}
              onSelect={setRange}
              min={1}
              disabled={{ before: new Date() }}
              numberOfMonths={isMobile ? 1 : 2}
              locale={dateFnsLocale}
              className={styles.calendar}
            />
          </div>
        )}
      </div>

      <div className={styles.actions}>
        {hasActiveSearch && (
          <button type="button" className={styles.clearButton} onClick={handleClear}>
            {t('clear')}
          </button>
        )}
        <button
          type="button"
          className={styles.searchButton}
          onClick={handleSearch}
          disabled={!canSearch}
        >
          {loading ? t('searching') : t('searchButton')}
        </button>
      </div>
    </div>
  );
}
